import { GetOptions, OrderCandidate } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getNextPageParam } from "../utils/page-params";

const databaseService = serviceHost.getDatabaseService();
const orderCandidateRepository =
  repositoryHost.getOrderCandidateRepository(databaseService);

export const useOrderCandidates = (options: GetOptions<OrderCandidate>) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ["order-candidates", "get", JSON.stringify(options)],
    [options],
  );

  useEffect(() => {
    const unsubscribe = databaseService.subscribeByQuery<OrderCandidate>(
      "order-candidates",
      options.queryConstraints || [],
      () => {
        queryClient.invalidateQueries({ queryKey });
      },
      options.orderBy,
    );

    return () => unsubscribe();
  }, [options.orderBy, options.queryConstraints, queryClient, queryKey]);

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      orderCandidateRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

export const useOrderCandidateById = (candidateId: string) => {
  return useQuery({
    queryKey: ["order-candidates", "get-by-id", candidateId],
    enabled: candidateId.length > 0,
    queryFn: () => orderCandidateRepository.get({ id: candidateId }),
  });
};
